import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { useLanguage } from '../../../contexts/LanguageContext';
import DiaryPhotoGallery from './DiaryPhotoGallery';

const WEATHER_COLORS = {
  Sunny: 'bg-yellow-100 text-yellow-800',
  Cloudy: 'bg-gray-100 text-gray-700',
  Rainy: 'bg-blue-100 text-blue-800',
  Windy: 'bg-cyan-100 text-cyan-800',
  Overcast: 'bg-slate-100 text-slate-700',
};

const WEATHER_ICONS = {
  Sunny: '☀️',
  Cloudy: '☁️',
  Rainy: '🌧️',
  Windy: '💨',
  Overcast: '🌥️',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr)?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return dateStr; }
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr)?.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return dateStr; }
};

// NSherjFh PR 12 — EntryDetailRow forwards canDeletePhoto + onPhoto
// Deleted to the embedded DiaryPhotoGallery so admin/SM/uploader gate
// + state-refresh callback flow through unchanged.
const EntryDetailRow = ({ entry, canDeletePhoto, onPhotoDeleted }) => {
  const { t } = useLanguage();
  const fields = [
    { label: t('labourOnSite'), value: entry?.labour_on_site },
    { label: t('deliveries'), value: entry?.deliveries },
    { label: t('workCompleted'), value: entry?.work_completed },
    { label: t('issuesDelays'), value: entry?.issues_delays },
    { label: t('generalNotes'), value: entry?.general_notes },
  ]?.filter(f => f?.value);

  return (
    <tr>
      <td colSpan={6} className="bg-muted/30 px-6 py-4 border-b border-border">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {fields?.length > 0 ? fields?.map(f => (
            <div key={f?.label}>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">{f?.label}</p>
              <p className="text-sm text-foreground whitespace-pre-wrap">{f?.value}</p>
            </div>
          )) : (
            <p className="text-sm text-muted-foreground col-span-2">{t('noAdditionalDetails')}</p>
          )}
        </div>
        {entry?.daily_site_photos?.length > 0 && (
          <div className="mt-4">
            <DiaryPhotoGallery
              photos={entry?.daily_site_photos}
              canDeletePhoto={canDeletePhoto}
              onPhotoDeleted={onPhotoDeleted}
            />
          </div>
        )}
      </td>
    </tr>
  );
};

// NSherjFh PR 12 — pass-through: DiaryListView forwards canDeletePhoto
// + onPhotoDeleted to both the desktop EntryDetailRow and the mobile
// expand-card gallery. Both render the same DiaryPhotoGallery and the
// state-update callback is identical.
const DiaryListView = ({ entries = [], sites = [], loading = false, onDelete, onRestore, canDeletePhoto, onPhotoDeleted }) => {
  const { t } = useLanguage();
  const [filterSite, setFilterSite] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const filtered = entries?.filter(e => {
    if (filterSite && e?.site_id !== filterSite) return false;
    if (filterDateFrom && e?.record_date < filterDateFrom) return false;
    if (filterDateTo && e?.record_date > filterDateTo) return false;
    return true;
  });

  const toggleExpand = (id) => setExpandedId(prev => prev === id ? null : id);

  const inputClass = 'px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent transition-colors';

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">{t('filterBySite')}</label>
          <select
            value={filterSite}
            onChange={e => setFilterSite(e?.target?.value)}
            className={inputClass}
          >
            <option value="">{t('allSites')}</option>
            {sites?.map(s => (
              <option key={s?.id} value={s?.id}>{s?.site_name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">{t('dateFrom')}</label>
          <input
            type="date"
            value={filterDateFrom}
            onChange={e => setFilterDateFrom(e?.target?.value)}
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">{t('dateTo')}</label>
          <input
            type="date"
            value={filterDateTo}
            onChange={e => setFilterDateTo(e?.target?.value)}
            className={inputClass}
          />
        </div>
        {(filterSite || filterDateFrom || filterDateTo) && (
          <button
            onClick={() => { setFilterSite(''); setFilterDateFrom(''); setFilterDateTo(''); }}
            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 pb-0.5"
          >
            <Icon name="X" size={14} /> {t('clear')}
          </button>
        )}
      </div>
      {/* Table — desktop */}
      <div className="hidden md:block overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('site')}</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('date')}</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('weather')}</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('submittedBy')}</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('created')}</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('details')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              Array.from({ length: 4 })?.map((_, i) => (
                <tr key={i}>
                  {Array.from({ length: 6 })?.map((__, j) => (
                    <td key={j} className="px-4 py-3">
                      <div className="h-4 bg-muted rounded animate-pulse" />
                    </td>
                  ))}
                </tr>
              ))
            ) : filtered?.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center">
                  <Icon name="BookOpen" size={32} className="mx-auto mb-3 text-muted-foreground/40" />
                  <p className="text-sm text-muted-foreground">{t('noDiaryEntriesFound')}</p>
                </td>
              </tr>
            ) : (
              filtered?.map(entry => (
                <React.Fragment key={entry?.id}>
                  <tr className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3 font-medium text-foreground">{entry?.sites?.site_name || '—'}</td>
                    <td className="px-4 py-3 text-muted-foreground">{formatDate(entry?.record_date)}</td>
                    <td className="px-4 py-3">
                      {entry?.weather ? (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${WEATHER_COLORS?.[entry?.weather] || 'bg-muted text-muted-foreground'}`}>
                          {WEATHER_ICONS?.[entry?.weather] || ''} {entry?.weather}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {entry?.user_profiles?.full_name || entry?.user_profiles?.email || '—'}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">{formatDateTime(entry?.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleExpand(entry?.id)}
                          className="flex items-center gap-1 text-accent hover:text-accent/80 text-xs font-medium transition-colors"
                        >
                          {expandedId === entry?.id ? (
                            <><Icon name="ChevronUp" size={14} /> {t('hide')}</>
                          ) : (
                            <><Icon name="ChevronDown" size={14} /> {t('view')}</>
                          )}
                        </button>
                        {/* NSherjFh PR 6: admin-only Delete (active view)
                            or Restore (deleted view), mutually exclusive
                            at the page level. */}
                        {onDelete && (
                          <button
                            onClick={() => onDelete(entry)}
                            className="flex items-center gap-1 text-destructive hover:text-destructive/80 text-xs font-medium transition-colors"
                            aria-label="Delete entry"
                          >
                            <Icon name="Trash2" size={14} />
                            Delete
                          </button>
                        )}
                        {onRestore && (
                          <button
                            onClick={() => onRestore(entry)}
                            className="flex items-center gap-1 text-accent hover:text-accent/80 text-xs font-medium transition-colors"
                            aria-label="Restore entry"
                          >
                            <Icon name="RotateCcw" size={14} />
                            Restore
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expandedId === entry?.id && (
                    <EntryDetailRow
                      entry={entry}
                      canDeletePhoto={canDeletePhoto}
                      onPhotoDeleted={onPhotoDeleted}
                    />
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
      {/* Cards — mobile */}
      <div className="md:hidden space-y-3">
        {loading ? (
          Array.from({ length: 3 })?.map((_, i) => (
            <div key={i} className="bg-card border border-border rounded-xl p-4 animate-pulse">
              <div className="h-4 bg-muted rounded w-1/2 mb-2" />
              <div className="h-3 bg-muted rounded w-1/3" />
            </div>
          ))
        ) : filtered?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="BookOpen" size={32} className="mx-auto mb-3 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">{t('noDiaryEntriesFound')}</p>
          </div>
        ) : (
          filtered?.map(entry => (
            <div key={entry?.id} className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <p className="font-semibold text-foreground text-sm">{entry?.sites?.site_name || '—'}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{formatDate(entry?.record_date)}</p>
                  </div>
                  {entry?.weather && (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${WEATHER_COLORS?.[entry?.weather] || 'bg-muted text-muted-foreground'}`}>
                      {WEATHER_ICONS?.[entry?.weather]} {entry?.weather}
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('by')} {entry?.user_profiles?.full_name || entry?.user_profiles?.email || '—'} · {formatDateTime(entry?.created_at)}
                </p>
                <div className="mt-3 flex items-center gap-3">
                  <button
                    onClick={() => toggleExpand(entry?.id)}
                    className="flex items-center gap-1 text-accent text-xs font-medium"
                  >
                    {expandedId === entry?.id ? (
                      <><Icon name="ChevronUp" size={13} /> {t('hideDetails')}</>
                    ) : (
                      <><Icon name="ChevronDown" size={13} /> {t('viewDetails')}</>
                    )}
                  </button>
                  {/* NSherjFh PR 6: mobile-card mirror of the desktop
                      Delete/Restore action. */}
                  {onDelete && (
                    <button
                      onClick={() => onDelete(entry)}
                      className="flex items-center gap-1 text-destructive text-xs font-medium"
                      aria-label="Delete entry"
                    >
                      <Icon name="Trash2" size={13} /> Delete
                    </button>
                  )}
                  {onRestore && (
                    <button
                      onClick={() => onRestore(entry)}
                      className="flex items-center gap-1 text-accent text-xs font-medium"
                      aria-label="Restore entry"
                    >
                      <Icon name="RotateCcw" size={13} /> Restore
                    </button>
                  )}
                </div>
              </div>
              {expandedId === entry?.id && (
                <div className="border-t border-border bg-muted/30 px-4 py-4 space-y-3">
                  {[
                    { label: t('labourOnSite'), value: entry?.labour_on_site },
                    { label: t('deliveries'), value: entry?.deliveries },
                    { label: t('workCompleted'), value: entry?.work_completed },
                    { label: t('issuesDelays'), value: entry?.issues_delays },
                    { label: t('generalNotes'), value: entry?.general_notes },
                  ]?.filter(f => f?.value)?.map(f => (
                    <div key={f?.label}>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-0.5">{f?.label}</p>
                      <p className="text-sm text-foreground whitespace-pre-wrap">{f?.value}</p>
                    </div>
                  ))}
                  {!entry?.labour_on_site && !entry?.deliveries && !entry?.work_completed && !entry?.issues_delays && !entry?.general_notes && !entry?.daily_site_photos?.length && (
                    <p className="text-sm text-muted-foreground">{t('noAdditionalDetails')}</p>
                  )}
                  {entry?.daily_site_photos?.length > 0 && (
                    <DiaryPhotoGallery
                      photos={entry?.daily_site_photos}
                      canDeletePhoto={canDeletePhoto}
                      onPhotoDeleted={onPhotoDeleted}
                    />
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
      {filtered?.length > 0 && (
        <p className="text-xs text-muted-foreground text-right">
          {t('showing')} {filtered?.length} {t('of')} {entries?.length} {t('entries')}
        </p>
      )}
    </div>
  );
};

export default DiaryListView;
